import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { useTranslation } from 'react-i18next'
import {
  wsManager,
  type NotificationMessage,
} from '@/services/websocket'
import { useToast } from '@/contexts/ToastContext'
import { useAuth } from '@/contexts/AuthContext'

export interface AppNotification extends NotificationMessage {
  read: boolean
  receivedAt: number
}

interface NotificationContextValue {
  items: AppNotification[]
  unreadCount: number
  markAllRead: () => void
  markRead: (uuid: string) => void
  clearAll: () => void
}

const NotificationContext = createContext<NotificationContextValue | null>(null)

export function NotificationProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation()
  const { pushToast } = useToast()
  const { isAuthenticated } = useAuth()
  const [items, setItems] = useState<AppNotification[]>([])

  useEffect(() => {
    if (!isAuthenticated) {
      setItems([])
      return
    }

    wsManager.connect()
    const unsub = wsManager.onMessage((messages) => {
      const stamped = messages.map<AppNotification>((m) => ({
        ...m,
        read: false,
        receivedAt: Date.now(),
      }))

      setItems((prev) => {
        const seen = new Set(prev.map((p) => p.uuid))
        const fresh = stamped.filter((s) => !seen.has(s.uuid))
        return [...fresh, ...prev].slice(0, 100)
      })

      stamped.forEach((n) => {
        pushToast({
          title: t(n.title, { defaultValue: n.title }),
          message: t(n.content, { defaultValue: n.content }),
          tone: 'info',
        })
      })
    })

    return () => {
      unsub()
    }
  }, [isAuthenticated, pushToast, t])

  const markAllRead = useCallback(() => {
    setItems((prev) => prev.map((i) => ({ ...i, read: true })))
  }, [])

  const markRead = useCallback((uuid: string) => {
    setItems((prev) =>
      prev.map((i) => (i.uuid === uuid ? { ...i, read: true } : i)),
    )
  }, [])

  const clearAll = useCallback(() => setItems([]), [])

  const unreadCount = useMemo(
    () => items.reduce((n, i) => n + (i.read ? 0 : 1), 0),
    [items],
  )

  const value = useMemo(
    () => ({ items, unreadCount, markAllRead, markRead, clearAll }),
    [items, unreadCount, markAllRead, markRead, clearAll],
  )

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  )
}

export function useNotifications(): NotificationContextValue {
  const ctx = useContext(NotificationContext)
  if (!ctx) {
    throw new Error('useNotifications must be used within NotificationProvider')
  }
  return ctx
}
